import {CircularProgress} from '@mui/material';
import {TargetBlank} from '@wandb/weave/common/util/links';
import React, {useMemo, useState} from 'react';
import ReactMarkdown from 'react-markdown';
import {Link} from 'react-router-dom';
import remarkGfm from 'remark-gfm';

import {MSG_INDENT_PL} from './constants';
import {AgentIcon, CheckIcon, ChevronIcon, UserIcon} from './icons';
import styles from './markdownProse.module.css';
import type {ChatMessage, ProgressStep, useWbAgentChat} from './useWbAgentChat';

type Chat = ReturnType<typeof useWbAgentChat>;

// ─── Inline step rendering ─────────────────────────────────────────

const ThinkingStep: React.FC<{step: ProgressStep; isLoading?: boolean}> = ({
  step,
  isLoading,
}) => (
  <div className="flex items-start gap-[5px] py-[2px]">
    {isLoading ? (
      <CircularProgress
        size={9}
        thickness={5}
        sx={{color: '#13A9BA', flexShrink: 0, marginTop: '2px'}}
      />
    ) : (
      <span className="mt-[2px] flex h-[12px] w-[12px] shrink-0 items-center justify-center text-[#B1B4B9]">
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
          <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.2" />
          <circle cx="4" cy="5.5" r="0.8" fill="currentColor" />
          <circle cx="6" cy="5.5" r="0.8" fill="currentColor" />
          <circle cx="8" cy="5.5" r="0.8" fill="currentColor" />
        </svg>
      </span>
    )}
    <span className="text-[11px] leading-[1.4] text-[#8F949E] italic">
      {step.label}
    </span>
  </div>
);

const ToolCallStep: React.FC<{step: ProgressStep; isLive?: boolean}> = ({
  step,
  isLive,
}) => {
  const [showOutput, setShowOutput] = useState(false);
  const pending = step.response === null;
  const hasResponse = step.response !== undefined && step.response !== null;
  return (
    <div className="my-[2px]">
      {/* Tool call: single compact line */}
      <div className="flex items-center gap-[5px] py-[2px]">
        {pending && isLive ? (
          <CircularProgress
            size={9}
            thickness={5}
            sx={{color: '#13A9BA', flexShrink: 0}}
          />
        ) : (
          <span className="flex h-[12px] w-[12px] shrink-0 items-center justify-center text-[#00B26E]">
            <CheckIcon />
          </span>
        )}
        <span className="shrink-0 text-[11px] font-medium text-[#555]">
          {step.label}
        </span>
        {step.detail && (
          <span className="truncate text-[11px] text-[#B1B4B9]">
            {step.detail}
          </span>
        )}
        {hasResponse && (
          <span
            onClick={() => setShowOutput(prev => !prev)}
            className="ml-auto shrink-0 cursor-pointer text-[10px] text-[#B1B4B9] hover:text-[#676D78]">
            {showOutput ? 'hide' : 'output'}
          </span>
        )}
      </div>
      {/* Tool response — hidden by default, toggled via "output" link */}
      {hasResponse && showOutput && (
        <div className="ml-[17px] mt-[1px] mb-[3px] max-h-[120px] overflow-auto rounded bg-[#F5F5F5] px-6 py-[4px]">
          <pre className="m-0 whitespace-pre-wrap break-words text-[10px] leading-[1.4] text-[#666]">
            {step.response}
          </pre>
        </div>
      )}
    </div>
  );
};

/** Render a list of progress steps inline. */
const StepsList: React.FC<{steps: ProgressStep[]; isLive?: boolean}> = ({
  steps,
  isLive,
}) => {
  if (steps.length === 0) {
    return null;
  }
  return (
    <div className="mb-[4px]">
      {steps.map((step, j) => {
        const isLast = j === steps.length - 1;
        if (step.type === 'thinking') {
          return (
            <ThinkingStep key={j} step={step} isLoading={isLive && isLast} />
          );
        }
        if (step.type === 'tool_call') {
          return <ToolCallStep key={j} step={step} isLive={isLive} />;
        }
        return null;
      })}
    </div>
  );
};

/** Collapsible steps block shown above the agent's final text. */
const CollapsibleSteps: React.FC<{steps: ProgressStep[]}> = ({steps}) => {
  const [expanded, setExpanded] = useState(true);
  const thinkingCount = steps.filter(s => s.type === 'thinking').length;
  const toolCount = steps.filter(s => s.type === 'tool_call').length;
  const parts: string[] = [];
  if (thinkingCount > 0) {
    parts.push(`${thinkingCount} thinking`);
  }
  if (toolCount > 0) {
    parts.push(`${toolCount} tool call${toolCount !== 1 ? 's' : ''}`);
  }
  const summary = parts.join(', ');

  return (
    <div className="mb-[6px]">
      <span
        onClick={() => setExpanded(prev => !prev)}
        className="inline-flex cursor-pointer select-none items-center gap-[3px] text-[11px] text-[#8F949E] hover:text-[#676D78]">
        <ChevronIcon expanded={expanded} />
        {summary}
      </span>
      {expanded && (
        <div className="mt-[2px]">
          <StepsList steps={steps} />
        </div>
      )}
    </div>
  );
};

// ─── LiveActivityFeed ───────────────────────────────────────────────

/** Whether the last step already carries its own loading indicator. */
const lastStepIsLoading = (steps: ProgressStep[]): boolean => {
  if (steps.length === 0) {
    return false;
  }
  const last = steps[steps.length - 1];
  return (
    last.type === 'thinking' ||
    (last.type === 'tool_call' && last.response === null)
  );
};

const LiveActivityFeed: React.FC<{
  steps: ProgressStep[];
  onCancel?: () => void;
}> = ({steps, onCancel}) => {
  const showWorkingBar = !lastStepIsLoading(steps);
  return (
    <div className="mb-2 mt-4">
      <div className="mb-[3px] flex items-center gap-6">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded bg-[#038194] text-white">
          <AgentIcon />
        </div>
        <span className="text-[12px] font-semibold text-[#2B3038]">
          WB Agent
        </span>
      </div>
      <div className={MSG_INDENT_PL}>
        {steps.length > 0 && <StepsList steps={steps} isLive />}

        {showWorkingBar && (
          <div className="flex items-center gap-6 py-[2px]">
            <CircularProgress
              size={9}
              thickness={5}
              sx={{color: '#13A9BA', flexShrink: 0}}
            />
            <span className="flex-1 text-[11px] text-[#8F949E]">
              Working...
            </span>
          </div>
        )}
        {onCancel && (
          <div className="flex justify-end py-[2px]">
            <span
              onClick={onCancel}
              className="cursor-pointer text-[11px] text-[#B1B4B9] hover:text-[#FF596D]">
              Stop
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── MessagesTab ────────────────────────────────────────────────────
export const MessagesTab: React.FC<{
  chat: Chat;
  isOpen: boolean;
  messagesContainerRef: React.RefObject<HTMLDivElement>;
}> = ({chat, isOpen, messagesContainerRef}) => {
  const mdComponents = useMemo(
    () => ({
      a: ({href, children}: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
        const isInternal =
          href &&
          (href.startsWith('/') || href.startsWith(window.location.origin));
        if (isInternal) {
          const path = href.startsWith('/')
            ? href
            : href.slice(window.location.origin.length);
          return <Link to={path}>{children}</Link>;
        }
        return <TargetBlank href={href}>{children}</TargetBlank>;
      },
    }),
    []
  );

  return (
    <div ref={messagesContainerRef} className="flex-1 overflow-auto p-12">
      {chat.available === null && isOpen && (
        <div className="flex justify-center pt-48">
          <CircularProgress size={20} sx={{color: '#13A9BA'}} />
        </div>
      )}
      {chat.available === false && (
        <div className="pt-48 text-center text-[#8F949E]">
          <p className="text-[13px]">
            WB Agent is not available for this project.
          </p>
        </div>
      )}
      {chat.available === true && chat.messages.length === 0 && (
        <div className="pt-48 text-center text-[#8F949E]">
          <p className="text-[13px]">Send a message to start a conversation.</p>
        </div>
      )}
      {chat.messages.map((msg, i) => {
        if (msg.role === 'steps') {
          return null;
        }
        if (msg.role === 'loading') {
          return (
            <LiveActivityFeed
              key={i}
              steps={msg.steps}
              onCancel={chat.cancel}
            />
          );
        }

        const precedingSteps =
          msg.role === 'agent' && i > 0 && chat.messages[i - 1].role === 'steps'
            ? (chat.messages[i - 1] as ChatMessage & {role: 'steps'}).steps
            : null;

        if (msg.role === 'user') {
          const prev = chat.messages[i - 1];
          const isNewTurn = prev && prev.role !== 'loading';
          return (
            <div key={i} className={`mb-2 ${isNewTurn ? 'mt-12' : 'mt-0'}`}>
              <div className="mb-[3px] flex items-center gap-6">
                <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded bg-[#D4D5D9] text-white">
                  <UserIcon />
                </div>
                <span className="text-[12px] font-semibold text-[#2B3038]">
                  You
                </span>
              </div>
              <div className={MSG_INDENT_PL}>
                <p className="whitespace-pre-wrap break-words text-[13px] leading-[1.5] text-[#2B3038]">
                  {msg.text}
                </p>
              </div>
            </div>
          );
        }

        // Agent message
        return (
          <div key={i} className="mb-2 mt-6">
            <div className="mb-[3px] flex items-center gap-6">
              <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded bg-[#038194] text-white">
                <AgentIcon />
              </div>
              <span className="text-[12px] font-semibold text-[#2B3038]">
                WB Agent
              </span>
            </div>
            <div className={MSG_INDENT_PL}>
              {precedingSteps && precedingSteps.length > 0 && (
                <CollapsibleSteps steps={precedingSteps} />
              )}
              <div className={styles.markdownProse}>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={mdComponents}>
                  {msg.text}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
